import { TwoUIBiCustomClick, useTwoUITelemetry } from "@amx/ux-twoui-telemetry";
import { Theme, useTheme } from "@amx/ux-twoui-theming";
import { OmitStrict } from "@amx/ux-twoui-utils";
import { HighContrastSelector, IIconProps, IMessageBarStyles, IRawStyle, mergeStyles, MessageBar as FluentMessageBar, MessageBarType as FluentMessageBarType } from "@fluentui/react";
import React from "react";
import { useComponentLogger } from "../../shared/logging";
import { BaseProps, InteractiveElementProps, validateProps } from "../../shared/props";
import { boxShadowDepths } from "../../styles/constants";
import { Button, ButtonProps } from "../Buttons/Button/Button";
import { IconProps } from "../Icon/Icon";
import { Hyperlink, HyperlinkProps, RoutelinkProps } from "../Links/Hyperlink/Hyperlink";
import { NoHrefLink, NoHrefLinkProps } from "../Links/NoHrefLink/NoHrefLink";
import { RichText, RichTextProps } from "../RichText/RichText";
import { Stack } from "../Stacks/Stack/Stack";
import { Text } from "../Text/Text";

export type MessageBarType = "info" | "secondary" | "warning" | "severe-warning" | "error" | "blocked" | "success";

const toFluentMessageBarType: { [K in MessageBarType]: FluentMessageBarType } = {
    "info": FluentMessageBarType.info,
    "secondary": FluentMessageBarType.info,
    "warning": FluentMessageBarType.warning,
    "severe-warning": FluentMessageBarType.severeWarning,
    "error": FluentMessageBarType.error,
    "blocked": FluentMessageBarType.blocked,
    "success": FluentMessageBarType.success
};

export interface MessageBarProps extends BaseProps {
    /**
     * Message content.
     * Can be a string or the props for a RichText component.
     */
    readonly messageContent: string | RichTextProps;

    /**
     * Message header
     */
    readonly messageHeader?: string;

    /**
     * MessageBar Type
     */
    readonly messageBarType?: MessageBarType;

    /**
     * Props for action link
     */
    readonly actionProps?: MessageBarActionProps;

    /**
     * Whether the message bar has a dismiss button and its props. If null, dismiss button doesn't show.
     */
    readonly dismissButtonProps?: DismissButtonProps;

    /**
     * Whether to hide icon on message bar
     */
    readonly hideIcon?: boolean;

    /**
     * Custom icon to replace the message bar icon. If unset, default will be the icon set by messageBarType.
     * Note: Fluent icons only
     */
    readonly iconProps?: Pick<IconProps, "iconName" | "color">;

    /**
     * Different Variants for messageBar.
     */
    readonly messageBarVariant?: MessageBarVariant;

    /**
     * Determines if the message bar is multi-lined.
     * If true, then the actions wrap to the next line.
     * If false, then actions appear next to the message.
     * By default this is false.
     */
    readonly isMultiline?: boolean;
}

export type MessageBarVariant = "floating" | "embedded-dialog" | "default";

export type MessageBarActionProps = HyperlinkActionProps | RoutelinkActionProps | NoHrefLinkActionProps | ButtonActionProps;

interface HyperlinkActionProps extends HyperlinkProps {
    readonly actionType?: "hyperlink";
}

interface RoutelinkActionProps extends RoutelinkProps {
    readonly actionType?: "routeLink";
}

interface NoHrefLinkActionProps extends NoHrefLinkProps {
    readonly actionType: "noHrefLink";
}

interface ButtonActionProps extends OmitStrict<ButtonProps, "type"> {
    readonly actionType: "button";
}

export interface DismissButtonProps extends InteractiveElementProps {
    /**
     * Callback of dismiss button and its callback.
     */
    readonly onDismiss: () => void;

    /**
     * Page area to be sent for BI
     */
    readonly pageArea: string;
}

const getMessageBarIconProps = (messageBarType: MessageBarType, theme: Theme, iconProps: Pick<IconProps, "iconName" | "color"> | undefined): IIconProps | undefined => {
    let messageBarIconProps: IIconProps | undefined;
    // overwrite the default icon color for warning message bar
    if (messageBarType === "warning") {
        messageBarIconProps = {
            iconName: "Info",
            styles: {
                root: {
                    color: theme.palette.neutralSecondary
                }
            }
        };
    }

    // respect any custom iconName and color
    if (iconProps) {
        messageBarIconProps = {
            iconName: iconProps.iconName,
            styles: {
                root: {
                    color:
                        iconProps.color ?
                            (theme.palette[iconProps.color] || theme.semanticColors[iconProps.color])
                            : theme.palette.neutralPrimary
                }
            }
        };
    }

    return messageBarIconProps;
};

const getMessageBarStyle = (
    theme: Theme,
    hideIcon?: boolean,
    messageHeader?: string,
    messageBarType?: MessageBarType,
    messageBarVariant?: MessageBarVariant
) => {

    const messageBarStyle: Partial<IMessageBarStyles> = {
        content: { padding: "8px 0" },
        actions: {
            alignItems: "center",
            marginRight: "24px",
            lineHeight: "16px"
        },
        innerText: {
            whiteSpace: "normal",
            overflow: "visible",
            width: "100%"
        },
        iconContainer: {
            alignItems: "center",
            marginLeft: hideIcon ? "0px" : "16px"
        },
        // The default link color (themePrimary) doesn't contrast strong enough against certain MessageBar backgrounds
        // so links inside MessageBar should have their color overridden to match how Fluent fixes this problem
        root: {
            ".ms-Link": {
                color: theme.semanticColors.messageLink
            },
            ".ms-Link > span": {
                color: theme.semanticColors.messageLink
            },
            [HighContrastSelector]: {
                ".ms-Link": {
                    color: "linktext"
                },
                ".ms-Link > span": {
                    color: "linktext"
                },
            }
        }
    };

    if (messageHeader) {
        messageBarStyle.actions = { alignItems: "flex-end", marginBottom: "8px", marginRight: "24px" };
        messageBarStyle.innerText = mergeStyles(messageBarStyle.innerText, { lineHeight: "normal" });
        messageBarStyle.icon = { lineHeight: "16px", paddingTop: "3px" };
    }

    if (hideIcon) {
        messageBarStyle.icon = { display: "none" };
        messageBarStyle.text = { marginLeft: "0px" };
    }

    if (messageBarType === "info") {
        messageBarStyle.root = { ...messageBarStyle.root as IRawStyle, background: theme.palette.neutralHigh };
    }

    if (messageBarVariant) {
        let variantStyle: IRawStyle | undefined;
        switch (messageBarVariant) {
            case "floating": {
                variantStyle = {
                    borderRadius: "8px",
                    boxShadow: boxShadowDepths.rest
                };
                break;
            }
            case "embedded-dialog": {
                variantStyle = {
                    borderRadius: "4px",
                    border: `1px solid ${theme.palette.neutralLighterAlt}`
                };
                break;
            }
        }
        if (variantStyle) {
            messageBarStyle.root = {
                ...messageBarStyle.root as IRawStyle,
                ...variantStyle
            };
        }
    }

    return messageBarStyle;
};

const getDismissButtonAriaLabel = (dismissButtonProps?: DismissButtonProps): string | undefined => {
    return dismissButtonProps?.["aria-label"];
};

export const MessageBar: React.FC<MessageBarProps> = ({
    messageContent,
    messageHeader,
    messageBarType = "info",
    actionProps,
    dismissButtonProps,
    hideIcon,
    iconProps,
    messageBarVariant = "default",
    isMultiline,
    ...otherProps
}) => {
    const twoUITelemetry = useTwoUITelemetry();
    useComponentLogger("MessageBar", otherProps.id);

    validateProps("MessageBar", otherProps);

    const theme = useTheme();

    const getLocalOnDismiss = () => {
        if (dismissButtonProps) {
            // create the custom click event
            const clickEvent: TwoUIBiCustomClick = {
                clickId: `${dismissButtonProps["data-bi-id"]}`,
                pageArea: `${dismissButtonProps.pageArea}`
            };
            // send the event
            twoUITelemetry.reportClick(clickEvent);

            dismissButtonProps.onDismiss();
        }
    };

    let actionLink: JSX.Element | undefined;
    if (actionProps) {
        if (actionProps.actionType === "noHrefLink") {
            const { actionType, ...baseActionProps } = actionProps;
            actionLink = <NoHrefLink {...baseActionProps} />;
        } else if (actionProps.actionType === "button") {
            const { actionType, ...baseActionProps } = actionProps;
            actionLink = <Button type="primaryAlt" {...baseActionProps} />;
        } else {
            const { actionType, ...baseActionProps } = actionProps;
            actionLink = <Hyperlink {...baseActionProps} />;
        }
    }

    let content: JSX.Element;
    if (typeof messageContent === "string") {
        content = <Text>{messageContent}</Text>;
    } else { // typeof messageContent === "RichTextProps"
        content = <RichText {...messageContent} />;
    }

    let messageBarIconProps: IIconProps | undefined;
    messageBarIconProps = getMessageBarIconProps(messageBarType, theme, iconProps);

    // interesting FluentUI behavior: isMultiline undefined is same as true
    return (
        <FluentMessageBar
            messageBarType={toFluentMessageBarType[messageBarType]}
            onDismiss={dismissButtonProps ? getLocalOnDismiss : undefined}
            dismissButtonAriaLabel={getDismissButtonAriaLabel(dismissButtonProps)}
            isMultiline={isMultiline ?? false}
            actions={actionLink}
            styles={getMessageBarStyle(
                theme,
                hideIcon,
                messageHeader,
                messageBarType,
                messageBarVariant
            )}
            messageBarIconProps={messageBarIconProps}
            {...otherProps}
        >
            <Stack childrenGap={6}>
                <Text variant="subjectTitle" block={true}>{messageHeader}</Text>
                {content}
            </Stack>
        </FluentMessageBar>
    );
};
